import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Camera, Upload, X, Check, Loader, SwitchCamera } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import imageStorage from '../utils/imageStorage';
import cloudSync from '../utils/cloudSync';

const ScanReceipt = () => {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [savedReceiptId, setSavedReceiptId] = useState(null); // Track if receipt was already saved via OCR
  const [merchant, setMerchant] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [businessExpense, setBusinessExpense] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch expense categories
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/expense');
        setCategories(response.data.map(c => c.name));
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Fallback to default categories
        setCategories(['Groceries', 'Transport', 'Eating Out', 'Entertainment', 'Healthcare', 'Shopping', 'Utilities', 'Uncategorized']);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup camera stream on unmount
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      // Request camera access with constraints that work on mobile
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Back camera preferred
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setStream(mediaStream);
      setShowCamera(true);
      
      // Set video source after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(err => {
            console.error('Video play error:', err);
          });
        }
      }, 100);
      
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera permission in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Failed to access camera: ' + error.message);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const capturedFile = new File([blob], 'receipt.jpg', { type: 'image/jpeg' });
        setFile(capturedFile);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          stopCamera();
        };
        reader.readAsDataURL(blob);
      }
    }, 'image/jpeg', 0.95);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleScan = async () => {
    if (!file) return;

    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/receipts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      setOcrResult(result);
      setSavedReceiptId(result.id); // Receipt is already saved in the database with image
      setMerchant(result.merchant);
      setDate(result.date);
      setAmount(result.amount.toString());
      setCategory(result.category);
      
      // Save image to IndexedDB for local storage
      if (preview) {
        try {
          const base64Data = preview.split(',')[1]; // Remove data:image/... prefix
          await imageStorage.saveImage(result.id, base64Data, {
            merchant: result.merchant,
            date: result.date,
            amount: result.amount
          });
          
          // For premium users, sync to cloud in background
          if (user?.subscription_status === 'active') {
            cloudSync.uploadToCloud(result.id).catch(err => {
              console.log('Cloud sync deferred:', err.message);
            });
          }
        } catch (storageError) {
          console.log('Local storage save skipped:', storageError.message);
        }
      }
      
      toast.success('Receipt scanned successfully!');
      refreshUser();
    } catch (error) {
      console.error('Scan error:', error);
      toast.error(error.response?.data?.detail || 'Failed to scan receipt');
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    if (saving) return; // Prevent double submit
    setSaving(true);
    
    try {
      // If receipt was already saved via OCR upload, just update it with any edits
      if (savedReceiptId) {
        await api.put(`/receipts/${savedReceiptId}`, {
          merchant,
          date,
          amount: parseFloat(amount),
          category,
          business_expense: businessExpense,
        });
        toast.success('Receipt saved!');
      } else {
        // Manual entry (no file uploaded) - create new receipt
        await api.post('/receipts/manual', {
          merchant,
          date,
          amount: parseFloat(amount),
          category,
          business_expense: businessExpense,
        });
        toast.success('Receipt saved!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save receipt');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    stopCamera();
    setFile(null);
    setPreview(null);
    setOcrResult(null);
    setSavedReceiptId(null);
    setMerchant('');
    setDate('');
    setAmount('');
    setCategory('');
    setBusinessExpense(false);
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
            Scan Receipt
          </h1>
          <p className="text-lg text-muted-foreground">
            Take a photo or upload a receipt for automatic recognition
          </p>
        </div>

        {/* Camera View */}
        {showCamera && (
          <div className="relative mb-6">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={(e) => e.target.play()}
              className="w-full h-auto rounded-2xl border border-white/10"
              style={{ maxHeight: '70vh', objectFit: 'cover' }}
              data-testid="camera-video"
            />
            <canvas ref={canvasRef} className="hidden" />
            
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-4">
              <button
                onClick={stopCamera}
                data-testid="close-camera-button"
                className="bg-red-500 hover:bg-red-600 text-white p-4 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <button
                onClick={capturePhoto}
                data-testid="capture-photo-button"
                className="bg-white hover:bg-white/90 text-black p-6 rounded-full transition-all transform hover:scale-105 shadow-lg"
              >
                <Camera className="w-8 h-8" />
              </button>
            </div>
          </div>
        )}

        {!preview && !showCamera ? (
          <div className="space-y-4">
            {/* Camera Button */}
            <button
              onClick={startCamera}
              data-testid="open-camera-button"
              className="w-full relative border-2 border-indigo-500/50 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent rounded-2xl p-12 text-center hover:border-indigo-500 hover:from-indigo-500/30 transition-all group"
            >
              <Camera className="w-16 h-16 mx-auto mb-4 text-indigo-400 group-hover:text-indigo-300 transition-colors" />
              <p className="text-lg font-medium mb-2">Take Photo with Camera</p>
              <p className="text-sm text-muted-foreground">
                Open camera to capture receipt directly
              </p>
            </button>

            {/* Upload Area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-area"
              className="relative border-2 border-dashed border-white/20 rounded-2xl p-12 text-center hover:border-indigo-500/50 hover:bg-white/5 transition-all cursor-pointer group"
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-muted-foreground group-hover:text-indigo-400 transition-colors" />
              <p className="text-lg font-medium mb-2">Or upload from gallery</p>
              <p className="text-sm text-muted-foreground">
                Supports JPG, PNG, WEBP (max 10MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                data-testid="file-input"
                className="hidden"
              />
            </div>

            {/* Manual Entry Button */}
            <button
              onClick={() => setOcrResult({ merchant: '', date: '', amount: 0, category: 'Uncategorized' })}
              data-testid="manual-entry-button"
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
            >
              <p className="font-medium">Or enter manually</p>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Preview */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img
                src={preview}
                alt="Receipt preview"
                className="w-full h-auto"
                data-testid="receipt-preview"
              />
              <button
                onClick={handleCancel}
                data-testid="remove-image-button"
                className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {!ocrResult && (
              <button
                onClick={handleScan}
                disabled={scanning}
                data-testid="scan-button"
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-4 rounded-full font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {scanning ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Scanning...</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    <span>Scan Receipt</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Edit Form */}
        {ocrResult && (
          <div className="mt-8 p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl space-y-4">
            <h2 className="text-2xl font-semibold mb-4">Review & Edit</h2>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Merchant Name
              </label>
              <input
                type="text"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                data-testid="input-merchant"
                placeholder="e.g., Walmart"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Date (MM/DD/YYYY)
              </label>
              <input
                type="text"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="input-date"
                placeholder="01/15/2026"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Amount (USD)
              </label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="input-amount"
                placeholder="0.00"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="select-category"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat} className="bg-[#0f172a]">
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="businessExpense"
                checked={businessExpense}
                onChange={(e) => setBusinessExpense(e.target.checked)}
                data-testid="checkbox-business-expense"
                className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
              <label htmlFor="businessExpense" className="text-sm text-muted-foreground">
                This is a business expense (for tax purposes)
              </label>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleCancel}
                data-testid="cancel-button"
                className="flex-1 bg-white/10 text-white hover:bg-white/20 px-6 py-3 rounded-full font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                data-testid="save-button"
                className="flex-1 bg-white text-black hover:bg-white/90 px-6 py-3 rounded-full font-medium transition-all flex items-center justify-center space-x-2"
              >
                <Check className="w-5 h-5" />
                <span>Save Receipt</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanReceipt;
